import { PIN_COLORS, PIN_DISPLAY_PRIORITY, BADGE_PILL_THRESHOLD, PULSE_WAVE_COUNT, PULSE_CYCLE_MS } from './constants'

/**
 * Renders a venue marker as a plain HTML element for an
 * `google.maps.marker.AdvancedMarkerElement`. Pure (DOM only, no React) so it is
 * unit-testable in jsdom and cheap to memoize.
 *
 * Layout (mirrors the mobile mini-pin marker):
 *   - circular venue logo token (border + shadow)
 *   - primary badge (top-right): highest-priority active count as a number
 *   - secondary pill (bottom): up to 4 small colored dots for remaining stats
 *   - optional CSS ripple beneath the token for live venues
 */

export interface MarkerPulse {
  active: boolean
  /** Ripple color (red for check-ins, blue for RSVPs). */
  color: string
}

export interface MarkerContentOptions {
  name: string
  logoUrl?: string | null
  /** 5-slot count array from computePinCounts (aligned to colors). */
  counts: (number | null)[]
  colors?: readonly string[]
  pulse?: MarkerPulse
}

/**
 * Stable cache key for a rendered marker: logoUrl + counts + colors + pulse.
 * Only rebuild a marker when this changes (mirrors mobile `miniPinsV8` key).
 */
export function markerCacheKey(opts: MarkerContentOptions): string {
  const colors = opts.colors ?? PIN_COLORS
  const counts = opts.counts.map(c => (c == null ? '' : c)).join(',')
  const pulse = opts.pulse?.active ? `pulse:${opts.pulse.color}` : 'nopulse'
  return `${opts.logoUrl ?? ''}|${counts}|${colors.join(',')}|${pulse}`
}

function initialsFor(name: string): string {
  const trimmed = (name ?? '').trim()
  return (trimmed.charAt(0) || '?').toUpperCase()
}

export function buildMarkerContent(opts: MarkerContentOptions): HTMLElement {
  const colors = opts.colors ?? PIN_COLORS
  const { counts } = opts

  const activeIndices = PIN_DISPLAY_PRIORITY.filter(i => {
    const c = counts[i]
    return c != null && c > 0
  })
  const primaryIndex = activeIndices.length > 0 ? activeIndices[0] : null
  const secondaryIndices = activeIndices.slice(1, 5) // up to 4 dots

  const wrapper = document.createElement('div')
  wrapper.className = 'live-marker'

  // Ripple (rendered first so it sits beneath the token in z-order).
  if (opts.pulse?.active) {
    const ripple = document.createElement('div')
    ripple.className = 'live-marker__ripple'
    ripple.style.setProperty('--ripple-color', opts.pulse.color)
    for (let i = 0; i < PULSE_WAVE_COUNT; i++) {
      const ring = document.createElement('span')
      ring.className = 'live-marker__ring'
      // Stagger waves by i/3 of the cycle, matching the mobile phase offset.
      ring.style.animationDelay = `${(i / PULSE_WAVE_COUNT) * PULSE_CYCLE_MS}ms`
      ripple.appendChild(ring)
    }
    wrapper.appendChild(ripple)
  }

  // Logo token.
  const token = document.createElement('div')
  token.className = 'live-marker__token'
  if (opts.logoUrl) {
    const img = document.createElement('img')
    img.className = 'live-marker__logo'
    img.src = opts.logoUrl
    img.alt = opts.name
    img.loading = 'lazy'
    token.appendChild(img)
  } else {
    const initials = document.createElement('span')
    initials.className = 'live-marker__initials'
    initials.textContent = initialsFor(opts.name)
    token.appendChild(initials)
  }
  wrapper.appendChild(token)

  // Primary numeric badge.
  if (primaryIndex != null) {
    const value = counts[primaryIndex] as number
    const badge = document.createElement('div')
    badge.className =
      'live-marker__badge' + (value >= BADGE_PILL_THRESHOLD ? ' live-marker__badge--pill' : '')
    badge.style.backgroundColor = colors[primaryIndex]
    badge.textContent = String(value)
    wrapper.appendChild(badge)
  }

  // Secondary dots pill.
  if (secondaryIndices.length > 0) {
    const pill = document.createElement('div')
    pill.className = 'live-marker__dots'
    for (const idx of secondaryIndices) {
      const dot = document.createElement('span')
      dot.className = 'live-marker__dot'
      dot.style.backgroundColor = colors[idx]
      pill.appendChild(dot)
    }
    wrapper.appendChild(pill)
  }

  return wrapper
}
